import React, { useEffect, useState } from 'react';
import {
  BrowserRouter as Router,
  Navigate,
  Route,
  Routes,
  useLocation,
} from 'react-router-dom';
import {
  AuthenticatedTemplate,
  UnauthenticatedTemplate,
  useIsAuthenticated,
  useMsal,
} from '@azure/msal-react';
import { CssBaseline, useMediaQuery } from '@mui/material';
import { Global } from '@emotion/react';
import { EventMessage, EventType } from '@azure/msal-browser';

import CustomSidebar from './presentation/layout/Sidebar/Sidebar';
import TopBar from './presentation/layout/TopBar/TopBar';
import TranslationModal from './presentation/features/translation/TranslationModal/TranslationModal';
import PageFooter from './presentation/layout/PageFooter/PageFooter';
import MobileMenu from './presentation/layout/MobileMenu/MobileMenu';
import WelcomeModal from './presentation/features/common_modals/WelcomeModal/WelcomeModal';
import {
  AppContainer,
  GlobalStyles,
  MainContent,
  SidebarContainer,
} from './styles/styles';
import { appInsightsManager } from './services/appInsights/appInsightsManager';
import {
  useContextStore,
  useProfileStore,
  useTranslationsStore,
} from './stores/storeRoot';
import { ArticleProvider } from './context/SelectedNewsContext';
import { AuthContextInitializator } from './context/AuthContextInitializator';
import {
  administrationPath,
  contactsAdminPath,
  dataProtectionPath,
  devPlaygroundPath,
  getPageName,
  imprintPath,
  individualDocumentsPath,
  loggedOutPath,
  loginPath,
  masterdataPath,
  myCampaignPath,
  myContactsPath,
  myContractsPath,
  myFieldsYieldsPath,
  myPaymentsPath,
  myTasksAdminPath,
  notFoundPath,
  orderedDeliveredProductsPath,
  redCertPath,
  rootPath,
  statisticAdminPath,
  surveyPath,
  termsPath,
  translationsAdminPath,
  userSettingsPath,
} from './routes';
import { useLoader } from './context/ApplicationLoaderContext';
import ApplicationLoader from './presentation/components/ApplicationLoader/ApplicationLoader';
import DataProtection from './pages/DataProtection/DataProtection';
import Home from './pages/Home/Home';
import Imprint from './pages/Imprint/Imprint';
import PageNotFound from './pages/PageNotFound/PageNotFound';
import TermsCondition from './pages/TermsCondition/TermsCondition';
import UnauthenticatedHome from './pages/UnauthenticatedHome/UnauthenticatedHome';
import MyOrders from './pages/MyOrders/MyOrders';
import MyCampaigns from './pages/MyCampaigns/MyCampaigns';
import MyPayments from './pages/MyPayments/MyPayments';
import UserSettings from './pages/UserSettings/UserSettings';
import MyContracts from './pages/MyContracts/MyContracts';
import Masterdata from './pages/Masterdata/Masterdata';
import MyContacts from './pages/MyContacts/MyContacts';
import {
  CropConnectCountry,
  CropConnectLanguages,
  CropConnectRoleDefinitions,
} from './services/cropConnectApiClient';
import MyFieldsYields from './pages/MyFieldsYields/MyFieldsYields';
import CookiebotScript from './presentation/features/settings/CookieDeclaration/CookieBanner';
import LoggedOutPage from './pages/LoggedOutPage/LoggedOutPage';
import ErrorBoundary from './presentation/components/ErrorBoundary/ErrorBoundary';
import InactivityTimerProvider from './context/InactivityTimerProvider';
import PublicPagesTranslationTool from './presentation/features/translation/PublicPagesTranslationTool/PublicPagesTranslationTool';
import Administration from './pages/Administration/Administration';
import {
  SecurityTrimmedRoute,
  SecurityTrimMode,
} from './services/auth/SecurityTrim';
import { sideBarAdministrationPathSecurityTrimProps } from './services/auth/predefinedPermissions/predefinedSecurityTrimProps';
import Survey from './pages/Survey/Survey';
import { myFieldsAndYieldsRequiredRoleDefinitions } from './services/auth/predefinedPermissions/predefinedRoleDefinitionsGroups';
import AdministrationStatistics from './pages/AdministrationStatistics/AdministrationStatistics';
// eslint-disable-next-line import/no-named-as-default
import AdministrationContacts from './pages/AdministrationContacts/AdministrationContacts';
import MyTasksAdmin from './presentation/features/my_tasks/MyTasksAdmin/MyTasksAdmin';
import EContractingPage from './pages/EContractingPage/EContractingPage';
import FDTMPage from './pages/FDTMPage/FDTMPage';
import Playground from './pages/Playground/Playground';
import Login from './pages/Login/Login';
import IndividualDocuments from './pages/IndividualDocuments/IndividualDocuments';
import ScrollToTop from './helpers/ScrollToTop';
import NonCloseableWelcomeModal from './presentation/features/common_modals/NonCloseableWelcomeModal/NonCloseableWelcomeModal';

const App: React.FC = () => {
  const { instance } = useMsal();
  const { loading, setLoading } = useLoader();
  const [signInInProgress, setSignInInProgress] = useState<boolean>(false);
  const {
    selectedUserLanguage,
    isInitialLogin,
    activeBpNumberCountry,
    fetchUser,
    fetchContext,
  } = useContextStore();
  const isAuthenticated = useIsAuthenticated();
  const { changeLanguage, convertCountryToLanguage } = useTranslationsStore();
  const { changeUserLanguage } = useProfileStore();
  const isMobile = useMediaQuery('(max-width:1200px)');
  const [isMenuOpen, setMenuOpen] = useState(false);
  const toggleMenu = () => setMenuOpen(!isMenuOpen);

  useEffect(() => {
    const callbackId = instance.addEventCallback((message: EventMessage) => {
      if (message.eventType === EventType.LOGIN_START) {
        setLoading(true);
      } else if (
        message.eventType === EventType.LOGIN_SUCCESS ||
        message.eventType === EventType.HANDLE_REDIRECT_END
      ) {
        setLoading(false);
      }
    });
    return () => {
      if (callbackId) {
        instance.removeEventCallback(callbackId);
      }
    };
  }, [instance, setLoading]);

  // TODO: Investigate if this setTimeout is still necessary.
  // We previously encountered an MSAL login issue without it,
  // so it was kept as a precaution. Review and remove if no longer needed.
  const initializeSignIn = () => {
    if (!signInInProgress) {
      setSignInInProgress(true);
      setLoading(true);
      setTimeout(() => {
        setSignInInProgress(false);
      }, 1000);
      instance.loginRedirect();
    }
  };

  useEffect(() => {
    if (!isAuthenticated) {
      setLoading(false);
    } else {
      const validLanguage = selectedUserLanguage as CropConnectLanguages;
      changeLanguage(validLanguage);
      Promise.all([fetchUser(), fetchContext()]).finally(() =>
        setLoading(false)
      );
    }
  }, [
    isAuthenticated,
    selectedUserLanguage,
    changeLanguage,
    fetchUser,
    fetchContext,
    setLoading,
  ]);

  const usePageTracking = () => {
    const location = useLocation();
    useEffect(() => {
      const startTime = Date.now();
      return () => {
        const duration = Date.now() - startTime;
        const pageName = getPageName(location.pathname);
        appInsightsManager._reactPlugin.trackPageView({
          name: pageName,
          uri: location.pathname,
          properties: { duration },
        });
      };
    }, [location]);
  };

  const PageTracking = () => {
    usePageTracking();
    return null;
  };

  const getQueryParam = (param: string) => {
    const searchParams = new URLSearchParams(window.location.search);
    return searchParams.get(param);
  };

  const forceShowModal = getQueryParam('forceShowModal');
  const forceShowNonCloseableModal = getQueryParam(
    'forceShowNonCloseableModal'
  );
  useEffect(() => {
    if (forceShowModal) {
      setIsWelcomeModalOpen(true);
    } else {
      setIsWelcomeModalOpen(isInitialLogin ?? false);
    }
  }, [forceShowModal, isInitialLogin]);

  useEffect(() => {
    if (forceShowNonCloseableModal) {
      setIsNonCloseableWelcomeModalOpen(true);
    } else {
      // TODO: Implement logic to show this non-closeable modal
      // setIsNonCloseableWelcomeModalOpen(isInitialLogin ?? false);
    }
  }, [forceShowNonCloseableModal, isInitialLogin]);
  const [isWelcomeModalOpen, setIsWelcomeModalOpen] = useState(false);
  const [isNonCloseableWelcomeModalOpen, setIsNonCloseableWelcomeModalOpen] =
    useState(false);

  useEffect(() => {
    // Change i18n and user language to the active BP country language on first login. If active BP country is not set,
    // it should've used EnGB als lang already, so nothing to do here then.
    if (isInitialLogin && activeBpNumberCountry) {
      const lang = convertCountryToLanguage(
        activeBpNumberCountry as CropConnectCountry
      );
      changeLanguage(lang);
      changeUserLanguage(lang);
    }
  }, [
    isInitialLogin,
    activeBpNumberCountry,
    changeLanguage,
    changeUserLanguage,
  ]);

  const handleWelcomeModalClose = () => {
    setIsWelcomeModalOpen(false);
  };

  return (
    <>
      {loading && <ApplicationLoader />}
      <CookiebotScript />
      <Router>
        <ScrollToTop />
        <ErrorBoundary>
          <PageTracking />
          <CssBaseline />
          <Global styles={GlobalStyles} />
          {!loading && (
            <AppContainer>
              <AuthenticatedTemplate>
                <InactivityTimerProvider />
                <WelcomeModal
                  open={isWelcomeModalOpen}
                  onClose={handleWelcomeModalClose}
                />
                <NonCloseableWelcomeModal
                  isOpen={isNonCloseableWelcomeModalOpen}
                />
                <AuthContextInitializator />
                <ArticleProvider>
                  <SidebarContainer>
                    <CustomSidebar />
                  </SidebarContainer>
                  <MainContent>
                    {isMobile && (
                      <MobileMenu
                        isMenuOpen={isMenuOpen}
                        onClose={toggleMenu}
                        onMenuClick={toggleMenu}
                      />
                    )}
                    <TranslationModal />
                    <Routes>
                      <Route path={rootPath} element={<Home />} />
                      <Route path={redCertPath} element={<Home />} />
                      <Route
                        path={orderedDeliveredProductsPath}
                        element={<MyOrders />}
                      />
                      <Route path={myContractsPath} element={<MyContracts />} />
                      <Route path={myPaymentsPath} element={<MyPayments />} />
                      <Route path={myCampaignPath} element={<MyCampaigns />} />
                      <Route path={masterdataPath} element={<Masterdata />} />
                      <Route
                        path={userSettingsPath}
                        element={<UserSettings />}
                      />
                      <Route path={myContactsPath} element={<MyContacts />} />
                      <Route
                        path={myFieldsYieldsPath}
                        element={
                          <SecurityTrimmedRoute
                            securityProperties={{
                              requiredBusinessPartnerRoles:
                                myFieldsAndYieldsRequiredRoleDefinitions,
                              requiredGlobalRoles: [
                                CropConnectRoleDefinitions.DocumentPublisher,
                              ],
                              securityTrimMode: SecurityTrimMode.OR,
                            }}
                            fallbackPath={rootPath}
                          >
                            <MyFieldsYields />
                          </SecurityTrimmedRoute>
                        }
                      />
                      <Route path={termsPath} element={<TermsCondition />} />
                      <Route
                        path={dataProtectionPath}
                        element={<DataProtection />}
                      />
                      <Route path={imprintPath} element={<Imprint />} />
                      <Route
                        path={administrationPath}
                        element={
                          <SecurityTrimmedRoute
                            securityProperties={
                              sideBarAdministrationPathSecurityTrimProps
                            }
                            fallbackPath={rootPath}
                          >
                            <Administration />
                          </SecurityTrimmedRoute>
                        }
                      />
                      <Route
                        path={contactsAdminPath}
                        element={<AdministrationContacts />}
                      />
                      <Route
                        path={loggedOutPath}
                        element={<Navigate to={rootPath} />}
                      />
                      <Route
                        path={loginPath}
                        element={<Navigate to={rootPath} />}
                      />
                      <Route
                        path={translationsAdminPath}
                        element={<PublicPagesTranslationTool />}
                      />
                      <Route
                        path={statisticAdminPath}
                        element={<AdministrationStatistics />}
                      />
                      <Route path={surveyPath} element={<Survey />} />
                      <Route path="field-nomination/*" element={<FDTMPage />} />
                      <Route
                        path="e-contracting/*"
                        element={<EContractingPage />}
                      />
                      <Route
                        path={devPlaygroundPath}
                        element={<Playground />}
                      />
                      <Route
                        path={myTasksAdminPath}
                        element={
                          <SecurityTrimmedRoute
                            securityProperties={{
                              requiredGlobalRoles: [
                                CropConnectRoleDefinitions.TaskCreator,
                              ],
                            }}
                            fallbackPath={rootPath}
                          >
                            <MyTasksAdmin />
                          </SecurityTrimmedRoute>
                        }
                      />
                      <Route
                        path={individualDocumentsPath}
                        element={<IndividualDocuments />}
                      />
                      <Route path={notFoundPath} element={<PageNotFound />} />
                    </Routes>
                    <PageFooter />
                  </MainContent>
                </ArticleProvider>
              </AuthenticatedTemplate>
              <UnauthenticatedTemplate>
                <SidebarContainer>
                  <CustomSidebar />
                </SidebarContainer>
                <MainContent>
                  <TopBar onLogin={initializeSignIn} />
                  <Routes>
                    <Route path={loginPath} element={<Login />} />
                    <Route path={rootPath} element={<UnauthenticatedHome />} />
                    <Route path={termsPath} element={<TermsCondition />} />
                    <Route
                      path={dataProtectionPath}
                      element={<DataProtection />}
                    />
                    <Route path={imprintPath} element={<Imprint />} />
                    <Route
                      path={loggedOutPath}
                      element={<LoggedOutPage onLogin={initializeSignIn} />}
                    />
                    <Route path="*" element={<UnauthenticatedHome />} />
                  </Routes>
                  <PageFooter />
                </MainContent>
              </UnauthenticatedTemplate>
            </AppContainer>
          )}
        </ErrorBoundary>
      </Router>
    </>
  );
};

export default App;
